import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface MonthlyPriceRow {
  year: number;
  month: number;
  area: string;
  avg_price: number | null;
  min_price: number | null;
  max_price: number | null;
  days: number;
}

export interface DailyPriceRow {
  price_date: string;
  area: string;
  avg_price: number | null;
  min_price: number | null;
  max_price: number | null;
}

export const MONTHS = [
  "januari",
  "februari",
  "mars",
  "april",
  "maj",
  "juni",
  "juli",
  "augusti",
  "september",
  "oktober",
  "november",
  "december",
];

export const AREAS = ["SE1", "SE2", "SE3", "SE4"];

export function monthSlug(month: number) {
  return MONTHS[month - 1];
}

/**
 * Året i en historiksida (/elpriser/historik/elpriser-2024), eller null om
 * slugen inte är ett år som sidorna visar (samma gränser som sidkomponenterna).
 */
export function historikYear(yearSlug?: string): number | null {
  const y = Number(String(yearSlug ?? "").replace(/^elpriser-/, ""));
  return y && y >= 2015 && y <= 2035 ? y : null;
}

export function monthFromSlug(slug?: string) {
  if (!slug) return null;
  const i = MONTHS.indexOf(slug.toLowerCase());
  return i === -1 ? null : i + 1;
}

export function monthLabel(month: number) {
  const m = MONTHS[month - 1] ?? "";
  return m.charAt(0).toUpperCase() + m.slice(1);
}

interface LegacyRow {
  year: number;
  month: number;
  area: string;
  avg_price: number | null;
}

async function fetchLegacyRows(filter?: { year?: number; month?: number }) {
  let q = supabase.from("monthly_price_history").select("year, month, area, avg_price");
  if (filter?.year) q = q.eq("year", filter.year);
  if (filter?.month) q = q.eq("month", filter.month);
  const { data } = await q;
  return ((data ?? []) as LegacyRow[]).map<MonthlyPriceRow>((r) => ({
    year: r.year,
    month: r.month,
    area: r.area,
    avg_price: r.avg_price,
    min_price: null,
    max_price: null,
    days: 0,
  }));
}

/** Merge legacy monthly averages in for year/month/area combos missing daily data. */
function mergeRows(primary: MonthlyPriceRow[], legacy: MonthlyPriceRow[]) {
  const seen = new Set(primary.map((r) => `${r.year}-${r.month}-${r.area}`));
  const merged = [...primary, ...legacy.filter((r) => !seen.has(`${r.year}-${r.month}-${r.area}`))];
  return merged.sort((a, b) => b.year - a.year || a.month - b.month || a.area.localeCompare(b.area));
}

/** All months that have stored spot price data, across all areas. */
export function useMonthlyPrices(year?: number) {
  const [rows, setRows] = useState<MonthlyPriceRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      let query = supabase
        .from("monthly_spot_prices")
        .select("year, month, area, avg_price, min_price, max_price, days")
        .order("year", { ascending: false })
        .order("month", { ascending: true });
      if (year) query = query.eq("year", year);
      const [{ data }, legacy] = await Promise.all([query, fetchLegacyRows(year !== undefined ? { year } : undefined)]);
      if (!active) return;
      setRows(mergeRows((data ?? []) as MonthlyPriceRow[], legacy));
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [year]);

  return { rows, loading };
}

/** Years that actually have data, newest first. */
export function useAvailableYears() {
  const [years, setYears] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      const [{ data }, { data: legacy }] = await Promise.all([
        supabase.from("monthly_spot_prices").select("year").order("year", { ascending: false }),
        supabase.from("monthly_price_history").select("year"),
      ]);
      if (!active) return;
      const all = [
        ...((data ?? []) as { year: number }[]),
        ...((legacy ?? []) as { year: number }[]),
      ].map((r) => r.year);
      setYears(Array.from(new Set(all)).sort((a, b) => b - a));
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  return { years, loading };
}


/** Daily averages for a specific month. */
export function useMonthDailyPrices(year: number, month: number) {
  const [rows, setRows] = useState<DailyPriceRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const from = `${year}-${String(month).padStart(2, "0")}-01`;
      const toDate = new Date(Date.UTC(year, month, 0));
      const to = `${year}-${String(month).padStart(2, "0")}-${String(toDate.getUTCDate()).padStart(2, "0")}`;
      const { data } = await supabase
        .from("spot_prices")
        .select("price_date, area, avg_price, min_price, max_price")
        .gte("price_date", from)
        .lte("price_date", to)
        .order("price_date");
      if (!active) return;
      setRows((data ?? []) as DailyPriceRow[]);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [year, month]);

  return { rows, loading };
}

/** Same month across all available years (for year-over-year comparison). */
export function useMonthAcrossYears(month: number) {
  const [rows, setRows] = useState<MonthlyPriceRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const [{ data }, legacy] = await Promise.all([
        supabase
          .from("monthly_spot_prices")
          .select("year, month, area, avg_price, min_price, max_price, days")
          .eq("month", month)
          .order("year", { ascending: false }),
        fetchLegacyRows({ month }),
      ]);
      if (!active) return;
      setRows(mergeRows((data ?? []) as MonthlyPriceRow[], legacy));

      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [month]);

  return { rows, loading };
}

export function averageOf(rows: { avg_price: number | null }[]) {
  const vals = rows.map((r) => r.avg_price).filter((v): v is number => v != null);
  if (vals.length === 0) return null;
  return Number((vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(2));
}
